// Botão padrão do painel admin.
//
// Completa a família `Admin*` (AdminInput, AdminSwitch, AdminCheckbox,
// AdminDatePicker, AdminAlert, AdminActionMenu). Existe porque, sem ele, as
// telas admin caíam em `components/Button` — que é o botão do ALUNO: fonte da
// marca do cliente, `fontSize` do cliente e `borderRadius` do cliente. Resultado:
// o mesmo formulário do painel mudava de cara a cada cliente.
//
// Cores e tipografia vêm só de ADMIN_COLORS/ADMIN_FONT. Regras e medidas em
// `docs/PADRAO-PAINEL-ADMIN.md`.

import React, { useCallback, useRef } from 'react';
import { Pressable, Text, ActivityIndicator, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ADMIN_COLORS, ADMIN_FONT } from '../../config/theme/adminColors';

const VARIANTS = {
  // Ação principal = TEAL. É o que a maior parte do painel já usa e o que o
  // token `primaryBg` sempre significou. O accent AZUL não é botão primário —
  // ele é seleção/foco/destaque de informação; existe como variante `accent`
  // só porque alguns modais mais novos o adotaram como CTA.
  primary: {
    bg: ADMIN_COLORS.primaryBg,
    bgHover: ADMIN_COLORS.primaryBgHover,
    text: ADMIN_COLORS.primaryText,
    border: ADMIN_COLORS.primaryBg,
  },
  accent: {
    bg: ADMIN_COLORS.accent,
    bgHover: ADMIN_COLORS.accentDark,
    text: ADMIN_COLORS.primaryText,
    border: ADMIN_COLORS.accent,
  },
  secondary: {
    bg: ADMIN_COLORS.secondaryBg,
    bgHover: '#E5E7EB',
    text: ADMIN_COLORS.secondaryText,
    border: ADMIN_COLORS.border,
  },
  danger: {
    bg: ADMIN_COLORS.danger,
    bgHover: '#B91C1C',
    text: '#FFFFFF',
    border: ADMIN_COLORS.danger,
  },
  // Ação destrutiva discreta: só texto/borda vermelhos, sem preencher.
  dangerGhost: {
    bg: 'transparent',
    bgHover: ADMIN_COLORS.dangerSoft,
    text: ADMIN_COLORS.danger,
    border: ADMIN_COLORS.dangerBorder,
  },
};

const SIZES = {
  default: { minHeight: 44, paddingHorizontal: 22, fontSize: 14, icon: 18 },
  compact: { minHeight: 40, paddingHorizontal: 14, fontSize: 13, icon: 16 },
};

const AdminButton = ({
  title,
  onPress,
  variant = 'primary',
  size = 'default',
  icon,
  fullWidth,
  isLoading = false,
  disabled = false,
  style,
  textStyle,
  ...rest
}) => {
  const v = VARIANTS[variant] || VARIANTS.primary;
  const s = SIZES[size] || SIZES.default;
  const blocked = isLoading || disabled;

  // Trava de reentrada contra duplo-clique (um "Salvar" clicado duas vezes vira
  // duas escritas). Vive num ref, então sobrevive aos re-renders — diferente do
  // `debounce(onPress, 500)` de `components/Button`, que é recriado a cada
  // render e por isso nunca chega a barrar o segundo clique.
  const lastPressRef = useRef(0);
  const handlePress = useCallback(
    (event) => {
      if (blocked || !onPress) return;
      const now = Date.now();
      if (now - lastPressRef.current < 500) return;
      lastPressRef.current = now;
      onPress(event);
    },
    [blocked, onPress],
  );

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityState={{ disabled: blocked, busy: isLoading }}
      disabled={blocked}
      onPress={handlePress}
      style={({ hovered, pressed }) => [
        styles.base,
        {
          minHeight: s.minHeight,
          paddingHorizontal: s.paddingHorizontal,
          backgroundColor: v.bg,
          borderColor: v.border,
        },
        fullWidth && { alignSelf: 'stretch' },
        (hovered || pressed) && !blocked && { backgroundColor: v.bgHover },
        blocked && { opacity: 0.6, cursor: 'not-allowed' },
        style,
      ]}
      {...rest}
    >
      {/* O spinner ocupa o LUGAR DO ÍCONE e o rótulo continua visível. Trocar o
          rótulo inteiro pelo spinner (como faz `components/Button`) encolhe o
          botão para o tamanho do spinner no meio do clique — o "Buscar
          informações do produto" viraria um círculo, e o usuário perde a
          referência do que está acontecendo. */}
      {isLoading ? (
        <ActivityIndicator size="small" color={v.text} />
      ) : icon ? (
        <Ionicons name={icon} size={s.icon} color={v.text} />
      ) : null}
      <Text
        numberOfLines={1}
        style={[styles.text, { fontSize: s.fontSize, color: v.text }, textStyle]}
      >
        {title}
      </Text>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  base: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderRadius: 10,
    borderWidth: 1,
    cursor: 'pointer',
  },
  text: {
    fontFamily: ADMIN_FONT,
    fontWeight: '600',
    // flexShrink: no RN o padrão é 0; sem isto um título longo estoura a pílula
    // em vez de truncar.
    flexShrink: 1,
  },
});

export default AdminButton;
